/**
 * "Em qual funil?" — a pergunta do botão Criar oportunidade do atendimento.
 *
 * ── Sugere, e não decide ─────────────────────────────────────────────────
 *
 * Decidido em 24/09/2026: o funil da regra do número por onde o cliente chegou
 * vem ESCOLHIDO, e a pessoa pode trocar. Quem está na conversa às vezes sabe o
 * que a regra não sabe: o cliente do número de vendas que escreveu para marcar
 * revisão.
 *
 * A tela diz de onde veio a sugestão. Um funil pré-escolhido sem explicação
 * parece padrão do sistema, e ninguém confere o que parece padrão.
 */

import { useId, useState, type ReactNode } from 'react';
import type { Funil } from '@cliqcrm/contracts';
import { Botao, CampoSelecao, Dialogo } from '@/shared/ui';

export interface FunilSugerido {
  readonly chave: string;
  /** A sugestão veio da regra do número, e não da falta dela. */
  readonly pelaRegra: boolean;
}

export function DialogoDeFunil({
  funis,
  sugerido,
  ocupado,
  aoCriar,
  aoFechar,
}: {
  funis: readonly Funil[];
  sugerido: FunilSugerido;
  ocupado: boolean;
  aoCriar: (funil: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const [funil, definirFunil] = useState(sugerido.chave);
  const descricao = useId();

  return (
    <Dialogo
      titulo="Criar oportunidade"
      forma="lateral"
      aoFechar={aoFechar}
      descricaoId={descricao}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            desabilitado={ocupado}
            motivo="Criando a oportunidade…"
            onClick={() => aoCriar(funil)}
          >
            {ocupado ? 'Criando…' : 'Criar'}
          </Botao>
        </>
      }
    >
      <p id={descricao}>
        {sugerido.pelaRegra
          ? 'O funil já vem escolhido pela regra do número por onde o cliente chegou. Troque se ' +
            'este cliente for de outro.'
          : 'Este número não tem regra de funil. Escolha onde a oportunidade entra.'}
      </p>
      <CampoSelecao
        rotulo="Em qual funil"
        value={funil}
        onChange={(e) => definirFunil(e.currentTarget.value)}
      >
        {funis.map((f) => (
          <option key={f.chave} value={f.chave}>
            {f.chave === sugerido.chave && sugerido.pelaRegra ? `${f.nome} (sugerido)` : f.nome}
          </option>
        ))}
      </CampoSelecao>
    </Dialogo>
  );
}

/**
 * O funil a sugerir: o da regra do número, se ele ainda existir; senão o
 * padrão; senão o primeiro. `null` sem funil nenhum.
 */
export function funilSugerido(
  funis: readonly Funil[],
  funilDaRegra: string | null | undefined,
): FunilSugerido | null {
  if (funilDaRegra && funis.some((f) => f.chave === funilDaRegra)) {
    return { chave: funilDaRegra, pelaRegra: true };
  }
  const reserva = funis.find((f) => f.chave === 'padrao') ?? funis[0];
  return reserva ? { chave: reserva.chave, pelaRegra: false } : null;
}
